import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { ResponsiveBar } from '@nivo/bar';
import { type TableColumnsType,Col, Row, Table } from 'antd';
import { Box, LoadingIndicator, QueryErrorAlert } from 'components';
import { useCustomTranslation } from 'helpers/translationHelper';
import { useGetCompaniesCodebookQuery } from 'pages/admin/companies/queries';
import React, { useMemo } from 'react';

import type { CompanyTrendStat } from '../../models';
import { useCompanyTrendsQuery, useTopCompaniesQuery } from '../../queries';
import { buildCompanyNameMap, resolveCompanyName } from './dashboardHelpers';
import DashboardRangeControls from './DashboardRangeControls';
import type { DashboardRange } from './useDashboardRange';

interface CompaniesTabProps {
  range: DashboardRange;
}

const CHART_HEIGHT = 400;

const CompaniesTab: React.FC<CompaniesTabProps> = ({ range }) => {
  const { tDashboard } = useCustomTranslation();
  const { from, to } = range;
  const topQuery = useTopCompaniesQuery(from, to);
  const trendsQuery = useCompanyTrendsQuery(from, to);
  const companiesQuery = useGetCompaniesCodebookQuery();

  const nameMap = useMemo(
    () => buildCompanyNameMap(companiesQuery.data),
    [companiesQuery.data]
  );

  // Reversed for the same reason as the dispatches ranking: a horizontal nivo bar chart draws the
  // first entry at the bottom, so the leader would otherwise sit at the bottom of the ranking.
  const topData = useMemo(
    () =>
      (topQuery.data ?? [])
        .map((c) => ({
          company: resolveCompanyName(nameMap, c.companyId),
          count: c.coreActivity
        }))
        .reverse(),
    [topQuery.data, nameMap]
  );

  const trendRows: CompanyTrendStat[] = useMemo(
    () =>
      (trendsQuery.data ?? []).map((row) => ({
        ...row,
        companyName: resolveCompanyName(nameMap, row.companyId)
      })),
    [trendsQuery.data, nameMap]
  );

  const columns: TableColumnsType<CompanyTrendStat> = useMemo(
    () => [
    {
      title: tDashboard('companies_name'),
      dataIndex: 'companyName',
      key: 'companyName'
    },
    {
      title: tDashboard('companies_current'),
      dataIndex: 'currentCount',
      key: 'currentCount',
      align: 'right',
      sorter: (a, b) => a.currentCount - b.currentCount
    },
    {
      title: tDashboard('companies_previous'),
      dataIndex: 'previousCount',
      key: 'previousCount',
      align: 'right',
      sorter: (a, b) => a.previousCount - b.previousCount
    },
    {
      title: tDashboard('companies_change'),
      dataIndex: 'changePercent',
      key: 'changePercent',
      align: 'right',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.changePercent - b.changePercent,
      render: (value: number) => {
        const positive = value >= 0;
        const color = positive ? '#3f8600' : '#cf1322';
        return (
          <span style={{ color }}>
            {positive ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(value)}%
          </span>
        );
      }
    }
    ],
    [tDashboard]
  );

  const error = topQuery.error || trendsQuery.error || companiesQuery.error;
  const isLoading =
    topQuery.isFetching ||
    trendsQuery.isFetching ||
    companiesQuery.isFetching ||
    !topQuery.data ||
    !trendsQuery.data;

  if (error) {
    return (
      <>
        <DashboardRangeControls {...range} showGranularity={false} />
        <QueryErrorAlert
          error={error}
          onRetry={() => {
            topQuery.refetch();
            trendsQuery.refetch();
            companiesQuery.refetch();
          }}
        />
      </>
    );
  }
  if (isLoading) {
    return (
      <>
        <DashboardRangeControls {...range} showGranularity={false} />
        <LoadingIndicator />
      </>
    );
  }

  return (
    <>
    <DashboardRangeControls {...range} showGranularity={false} />
    <Row gutter={16}>
      <Col md={24} lg={10}>
        <Box>
          <h4>{tDashboard('companies_topActive')}</h4>
          <p>{tDashboard('companies_topActive_hint')}</p>
          <div style={{ height: CHART_HEIGHT }}>
            <ResponsiveBar
              data={topData}
              keys={['count']}
              indexBy="company"
              layout="horizontal"
              margin={{ top: 20, right: 20, bottom: 40, left: 160 }}
              padding={0.3}
              enableLabel
              colors={{ scheme: 'nivo' }}
              axisBottom={{ tickValues: 5 }}
            />
          </div>
        </Box>
      </Col>
      <Col md={24} lg={14}>
        <Box>
          <h4>{tDashboard('companies_trendTitle')}</h4>
          <p>{tDashboard('companies_trendTitle_hint')}</p>
          <Table
            rowKey="companyId"
            size="small"
            columns={columns}
            dataSource={trendRows}
            pagination={false}
          />
        </Box>
      </Col>
    </Row>
    </>
  );
};

export default CompaniesTab;
